import React from 'react';
import logo from '../assets/primary_red.svg';
import config from '../utils/config';
import { logout } from '../services/authService';

import { Dialog, useMediaQuery, Box, Typography, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Slide from '@mui/material/Slide';

import { Button, ArrowLeftIcon, Select, SelectOption } from './ui';
import formatProductName from '../utils/formatProductName';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="right" ref={ref} {...props} />;
});

const Menu = ({
    open,
    handleClose,
    selectedAction,
    handleActionButtonClick,
    selectedProduct,
    handleProductButtonClick,
    selectedVersion,
    handleVersionButtonClick,
    filteredVersions,
    language,
    handleLanguageChange,
    username,
    products,
    buildNumber,
    animated
}) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [selectedLogOut, setSelectedLogOut] = React.useState(false);

    const texts = config.languages.find(lang => lang.code === language).texts;

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            transitionDuration={animated ? undefined : 0}
        >
            <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

                {/* LEFT PANEL — brand + welcome */}
                {!isSmallScreen && (
                    <Box sx={{
                        width: '35%',
                        minWidth: 340,
                        bgcolor: '#330000',
                        display: 'flex',
                        flexDirection: 'column',
                        p: 5,
                        position: 'relative',
                        overflow: 'hidden',
                    }}>
                        {/* Logo */}
                        <Box>
                            <img src={logo} alt="Honeywell" width={220} style={{ filter: 'brightness(0) invert(1)', marginLeft: '-20px' }} />
                        </Box>

                        {/* Welcome content */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Typography sx={{
                                color: 'white',
                                letterSpacing: 3,
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                mb: 1.5,
                            }}>
                                Welcome Back
                            </Typography>
                            <Typography variant="h3" sx={{
                                color: 'white',
                                fontWeight: 700,
                                lineHeight: 1.15,
                                mb: 2,
                                wordBreak: 'break-word',
                            }}>
                                {username}
                            </Typography>
                            <Box sx={{ width: 40, height: 3, bgcolor: '#cc0000', borderRadius: 1, mb: 3 }} />
                            <Typography sx={{
                                color: 'rgba(255,255,255,0.5)',
                                fontSize: '0.95rem',
                                lineHeight: 1.75,
                                maxWidth: 300,
                            }}>
                                Your intelligent assistant for Honeywell security systems. Configure your session to get started.
                            </Typography>
                        </Box>

                        {/* Footer */}
                        {/* <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '11px' }}>
                            {texts.honeywell} {texts.rights}
                        </Typography> */}
                    </Box>
                )}

                {/* RIGHT PANEL — form */}
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: '#f5f5f7',
                    overflow: 'auto',
                }}>
                    {/* Top bar */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        px: 3,
                        pt: 2,
                        pb: 1,
                    }}>
                        <Button
                            aria-label="Back"
                            icon={<ArrowLeftIcon fontSize={'28px'} strokeWidth={1.5} />}
                            iconPosition="left"
                            size="regular"
                            variant="quiet-secondary"
                            onClick={handleClose}
                        />
                        {isSmallScreen && (
                            <img src={logo} alt="Honeywell" width={130} />
                        )}
                    </Box>

                    {/* Form card */}
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 3,
                        py: 4,
                    }}>
                        <Box sx={{
                            bgcolor: 'white',
                            borderRadius: 3,
                            p: { xs: 3, sm: 5 },
                            width: '100%',
                            maxWidth: 420,
                            boxShadow: '0 4px 32px rgba(0,0,0,0.08)',
                        }}>
                            {/* Mobile-only welcome */}
                            {isSmallScreen && (
                                <Box mb={3}>
                                    <Typography sx={{
                                        color: '#cc0000',
                                        fontSize: '0.7rem',
                                        fontWeight: 700,
                                        letterSpacing: 2,
                                        textTransform: 'uppercase',
                                        mb: 0.5,
                                    }}>
                                        Welcome Back
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700}>
                                        {username}
                                    </Typography>
                                </Box>
                            )}

                            <Typography variant="h5" fontWeight={700} mb={0.5}>
                                {texts.lenels2agent}
                            </Typography>
                            <Typography color="text.secondary" fontSize="0.875rem" mb={4}>
                                Configure your session below.
                            </Typography>

                            {/* System */}
                            <Box mb={2.5}>
                                <Typography variant="body2" fontWeight={600} mb={0.75} color="text.primary">
                                    {texts.system}
                                </Typography>
                                <Select
                                    aria-label="System"
                                    value={selectedProduct || ''}
                                    placeholder="Select a system"
                                    id="system-select"
                                    onChange={(e) => handleProductButtonClick(e.target.value)}
                                    size="regular"
                                >
                                    {Object.keys(config.products).map((product) => (
                                        <SelectOption
                                            key={product}
                                            label={formatProductName(product)}
                                            value={product}
                                        />
                                    ))}
                                </Select>
                            </Box>

                            {/* Version */}
                            {filteredVersions.filter(v => v !== '').length > 0 && (
                                <Box mb={2.5}>
                                    <Typography variant="body2" fontWeight={600} mb={0.75} color="text.primary">
                                        {texts.version}
                                    </Typography>
                                    <Select
                                        aria-label="Version"
                                        value={selectedVersion || ''}
                                        placeholder="Select a version"
                                        id="version-select"
                                        onChange={(e) => handleVersionButtonClick(e.target.value)}
                                        size="regular"
                                    >
                                        {filteredVersions.filter(v => v !== '').map((version) => (
                                            <SelectOption
                                                key={version}
                                                label={version}
                                                value={version}
                                            />
                                        ))}
                                    </Select>
                                </Box>
                            )}

                            {/* Language */}
                            <Box mb={3.5}>
                                <Typography variant="body2" fontWeight={600} mb={0.75} color="text.primary">
                                    {texts.language}
                                </Typography>
                                <Select
                                    aria-label="Language"
                                    defaultValue={language}
                                    id="language-select"
                                    onBlur={function noRefCheck() {}}
                                    onChange={function noRefCheck() {}}
                                    onFocus={function noRefCheck() {}}
                                    placeholder="Select a language"
                                    size="regular"
                                >
                                    {config.languages.map(lang => (
                                        <SelectOption
                                            key={lang.code}
                                            label={lang.name}
                                            value={lang.code}
                                        />
                                    ))}
                                </Select>
                            </Box>

                            {/* Confirm */}
                            <Box display="flex" justifyContent="center" mb={3}>
                                <Button
                                    size="regular"
                                    variant="secondary"
                                    onClick={handleClose}
                                >
                                    {texts.confirm}
                                </Button>
                            </Box>

                            <Divider sx={{ mb: 3 }} />

                            {/* Sign out */}
                            <Box display="flex" justifyContent="center">
                                <Button
                                    size="regular"
                                    variant="quiet-secondary"
                                    onClick={() => {
                                        setSelectedLogOut(true);
                                        logout();
                                    }}
                                >
                                    {texts.signout}
                                </Button>
                            </Box>
                        </Box>
                    </Box>

                    {/* Legal footer */}
                    <Box sx={{ textAlign: 'center', px: 3, pb: 3 }}>
                        <Typography fontSize="11px" color="text.disabled" mb={0.5}>
                            {texts.honeywell} {texts.rights}
                        </Typography>
                        <Typography fontSize="11px" fontWeight={600}>
                            <a href={config.privacy_statement_url} target="_blank" rel="noopener noreferrer">
                                {texts.privacynotice}
                            </a>
                            {' • '}
                            <a href={config.eula_statement_url} target="_blank" rel="noopener noreferrer">
                                {texts.eulanotice}
                            </a>
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
};

export default Menu;
